import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Divider,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Kbd,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tag,
    TagLabel,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { Fragment, useCallback, useState } from 'react';

import { useAsset } from '../../api/asset';
import { formatDate, parseDate } from '../../utils/dateUtils';
import { getColumnLabel } from '../../utils/tableUtils';
import { AssetClassTag } from '../../widgets/AssetClassTag';
import { NumberCell } from '../../widgets/Table/NumberCell';
import { AssetForm } from './AssetForm';
import { AssetPositionsTable } from './AssetPositionsTable';
import { AssetPricing } from './AssetPricing';

export const AssetDrawer = ({ isOpen, onClose, sId }: { isOpen: boolean; onClose: () => void; sId?: string }) => {
    const { data: assetData } = useAsset({ sId });

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />

                <DrawerHeader borderBottomWidth={1}>
                    <Stack gap={1}>
                        <Text flexShrink={0} mb={1}>
                            Security details
                        </Text>
                        <Flex alignItems="center" gap={2} overflow="hidden">
                            <Flex alignItems="center" gap={1}>
                                {assetData?.assetClass && (
                                    <AssetClassTag size="md" assetClass={assetData?.assetClass} />
                                )}
                                {assetData?.assetType && (
                                    <Tag size="md" flexShrink={0}>
                                        <TagLabel>{assetData?.assetType}</TagLabel>
                                    </Tag>
                                )}
                                {assetData?.subAssetType && (
                                    <Tag size="md" flexShrink={0}>
                                        <TagLabel>{assetData?.subAssetType}</TagLabel>
                                    </Tag>
                                )}
                            </Flex>

                            <Divider orientation="vertical" height={5} />

                            <Flex alignItems="center" gap={1}>
                                {assetData?.symbol && (
                                    <Tag size="md" flexShrink={0}>
                                        <TagLabel>
                                            {assetData.primaryExchange
                                                ? `${assetData.primaryExchange}:${assetData.symbol}`
                                                : assetData.symbol}
                                        </TagLabel>
                                    </Tag>
                                )}
                                {assetData?.cusip && (
                                    <Tag size="md" flexShrink={0}>
                                        <TagLabel>{assetData.cusip}</TagLabel>
                                    </Tag>
                                )}
                            </Flex>

                            <Divider orientation="vertical" height={5} />

                            {assetData?.description && (
                                <Text fontWeight="medium" color="gray.400" fontSize="sm" noOfLines={1}>
                                    {assetData.description}
                                </Text>
                            )}
                        </Flex>

                        <Flex alignItems="center" gap={2}>
                            {[
                                assetData?.maturityDate && (
                                    <Text fontWeight="medium" color="gray.400" fontSize="sm" noOfLines={1}>
                                        {getColumnLabel('maturityDate')}{' '}
                                        <Tag size="sm" flexShrink={0}>
                                            <TagLabel>{formatDate(parseDate(assetData.maturityDate))}</TagLabel>
                                        </Tag>
                                    </Text>
                                ),
                                assetData?.expirationDate && (
                                    <Text fontWeight="medium" color="gray.400" fontSize="sm" noOfLines={1}>
                                        {getColumnLabel('expirationDate')}{' '}
                                        <Tag size="sm" flexShrink={0}>
                                            <TagLabel>{formatDate(parseDate(assetData.expirationDate))}</TagLabel>
                                        </Tag>
                                    </Text>
                                ),
                                assetData?.underlyingSymbol && (
                                    <Text fontWeight="medium" color="gray.400" fontSize="sm" noOfLines={1}>
                                        {getColumnLabel('underlyingSymbol')}{' '}
                                        <Tag size="sm" flexShrink={0}>
                                            <TagLabel>{assetData.underlyingSymbol}</TagLabel>
                                        </Tag>
                                    </Text>
                                ),
                                assetData?.coupon && (
                                    <Text fontWeight="medium" color="gray.400" fontSize="sm" noOfLines={1}>
                                        {getColumnLabel('coupon')}{' '}
                                        <Tag size="sm" flexShrink={0}>
                                            <TagLabel>
                                                <NumberCell value={assetData.coupon} />
                                            </TagLabel>
                                        </Tag>
                                    </Text>
                                ),
                            ]
                                .filter(Boolean)
                                .map((item, idx) => (
                                    <Fragment key={idx}>
                                        {idx !== 0 && <Divider orientation="vertical" height={4} />}
                                        {item}
                                    </Fragment>
                                ))}
                        </Flex>
                    </Stack>
                </DrawerHeader>

                {assetData && (
                    <DrawerBody px={0} pt={0}>
                        <Tabs colorScheme="teal" isFitted isLazy>
                            <TabList>
                                <Tab>Details</Tab>
                                <Tab>Pre-Trade Positions</Tab>
                                <Tab>Pricing</Tab>
                            </TabList>

                            <TabPanels>
                                <TabPanel>
                                    <AssetForm assetData={assetData} />
                                </TabPanel>

                                <TabPanel>
                                    <AssetPositionsTable sId={assetData.sId} />
                                </TabPanel>
                                <TabPanel>
                                    <AssetPricing assetData={assetData} />
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </DrawerBody>
                )}

                <DrawerFooter gap={4} justifyContent="space-between">
                    <Box>
                        <Alert status="info" fontSize="xs">
                            <AlertIcon boxSize={4} />
                            <Text>
                                You can close this panel by hitting <Kbd>Esc</Kbd> or clicking the backdrop
                            </Text>
                        </Alert>
                    </Box>
                    <Button variant="outline" onClick={onClose} flexShrink={0}>
                        Close
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export const useAssetDrawerControls = () => {
    const { isOpen, onOpen, onClose: onDisclosureClose } = useDisclosure();
    const [sId, setSId] = useState<string | undefined>();

    const viewAsset = useCallback(
        (asset: string) => {
            setSId(asset);
            onOpen();
        },
        [onOpen],
    );

    const onClose = () => {
        onDisclosureClose();
        setSId(undefined);
    };

    return {
        viewAsset,
        isOpen,
        onClose,
        sId,
    };
};
